import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, ConfirmDelete, usePaginated, Paginator } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Building2, Download, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDuplicateGuard, DuplicateCustomerDialog } from "@/components/DuplicateCustomer";

const empty = { name: "", is_company: true, ico: "", dic: "", ic_dph: "", address: "", city: "", psc: "", country: "SK", email: "", phone: "", note: "" };

export default function Customers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { dup, setDup, guard } = useDuplicateGuard();

  const load = useCallback(async () => {
    const { data } = await api.get("/customers", { params: { q } });
    setItems(data);
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const pg = usePaginated(items);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...empty, ...c }); setEditId(c.id); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookup = async () => {
    setLookupLoading(true);
    try {
      const { data } = await api.get("/lookup/company", { params: { country: form.country, q: form.ico || form.name } });
      if (data.found) {
        setForm((f) => ({ ...f, name: data.name || f.name, ico: data.ico || f.ico, dic: data.dic || f.dic, ic_dph: data.ic_dph || f.ic_dph, address: data.address || f.address, city: data.city || f.city, psc: data.psc || f.psc, country: data.country || f.country }));
        toast.success("Údaje firmy doplnené");
      } else {
        toast.warning(data.message || "Firma sa nenašla – doplňte ručne");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLookupLoading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Zadajte názov / meno"); return; }
    const orig = items.find((c) => c.id === editId);
    if (editId && orig && orig.name === form.name) { await doSave(); return; }
    setSaving(true);
    try { await guard(form.name, editId, doSave); } finally { setSaving(false); }
  };
  const doSave = async () => {
    setSaving(true);
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success("Uložené");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/customers/${id}`); toast.success("Zmazané"); load(); };

  return (
    <div>
      <PageHeader title="Zákazníci" subtitle="Firmy a fyzické osoby"
        action={<Button className="min-h-[48px] gap-2" onClick={openNew} data-testid="new-customer-button"><Plus className="h-5 w-5" /> Nový zákazník</Button>} />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="min-h-[48px] pl-9" placeholder="Hľadať názov, IČO, mesto…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.length ? pg.paged.map((c) => (
          <div key={c.id} className="card-surface cursor-pointer p-4 transition-colors hover:border-primary/50" onClick={() => navigate(`/zakaznici/${c.id}`)} data-testid={`customer-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 font-heading text-lg font-bold"><Building2 className="h-4 w-4 text-primary" />{c.name}</div>
              <span className="rounded bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase">{c.country}</span>
            </div>
            <div className="mt-2 space-y-0.5 text-sm text-muted-foreground">
              {c.ico && <div>IČO: <span className="font-mono text-foreground">{c.ico}</span></div>}
              {c.ic_dph && <div>IČ DPH: <span className="font-mono text-foreground">{c.ic_dph}</span></div>}
              {(c.address || c.city) && <div>{[c.address, c.psc, c.city].filter(Boolean).join(", ")}</div>}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="secondary" size="sm" className="gap-1" onClick={(e) => { e.stopPropagation(); openEdit(c); }} data-testid={`edit-customer-${c.id}`}><Pencil className="h-3.5 w-3.5" /> Upraviť</Button>
              <span onClick={(e) => e.stopPropagation()}>
                <ConfirmDelete onConfirm={() => remove(c.id)} description={`Zákazník „${c.name}“ bude natrvalo odstránený.`}>
                  <Button variant="ghost" size="sm" data-testid={`delete-customer-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                </ConfirmDelete>
              </span>
            </div>
          </div>
        )) : null}
        {!items.length && <div className="col-span-full py-12 text-center text-muted-foreground">Žiadni zákazníci.</div>}
      </div>

      <Paginator {...pg} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">{editId ? "Upraviť zákazníka" : "Nový zákazník"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Krajina</Label>
                <Select value={form.country} onValueChange={(v) => set("country", v)}>
                  <SelectTrigger data-testid="customer-country"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="SK">SK (RPO)</SelectItem><SelectItem value="CZ">CZ (ARES)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>IČO</Label>
                <div className="flex gap-2">
                  <Input value={form.ico} onChange={(e) => set("ico", e.target.value)} data-testid="customer-ico" />
                  <Button type="button" variant="outline" size="icon" onClick={lookup} disabled={lookupLoading} title="Doplniť z registra" data-testid="customer-lookup-button">
                    {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Názov / Meno</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="customer-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>DIČ</Label><Input value={form.dic} onChange={(e) => set("dic", e.target.value)} data-testid="customer-dic" /></div>
              <div className="space-y-1.5"><Label>IČ DPH</Label><Input value={form.ic_dph} onChange={(e) => set("ic_dph", e.target.value)} data-testid="customer-icdph" /></div>
            </div>
            <div className="space-y-1.5"><Label>Adresa</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="customer-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>PSČ</Label><Input value={form.psc} onChange={(e) => set("psc", e.target.value)} data-testid="customer-psc" /></div>
              <div className="space-y-1.5"><Label>Mesto</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="customer-city" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="customer-email" /></div>
              <div className="space-y-1.5"><Label>Telefón</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="customer-phone" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Zrušiť</Button>
            <Button onClick={save} disabled={saving} data-testid="save-customer-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateCustomerDialog dup={dup} setDup={setDup} onOpenExisting={(c) => { setOpen(false); navigate(`/zakaznici/${c.id}`); }} />
    </div>
  );
}
